import { useEffect, useReducer, useRef } from 'react';
import { api } from '@/lib/api/client';

interface Contract {
  id: number;
  name: string;
  type: string;
  currency: string;
  /** USD→contract-currency rate, needed to show a USD package cap in UZS. */
  dolarRate?: number;
  createdAt: string;
  hourlyPackageId?: number;
}

interface HourlyPackage {
  id: number;
  hours: number;
  price: number;
  /** Spelled `curency` in the API/Prisma model. */
  curency?: 'USD' | 'UZS';
  status?: string;
}

interface ContractState {
  selectedContract: Contract | null;
  hourlyPackage: HourlyPackage | null;
}

type ContractAction =
  | { type: 'SET_CONTRACT'; contract: Contract; hourlyPackage: HourlyPackage | null }
  | { type: 'CLEAR' };

function contractReducer(_state: ContractState, action: ContractAction): ContractState {
  switch (action.type) {
    case 'SET_CONTRACT':
      return { selectedContract: action.contract, hourlyPackage: action.hourlyPackage };
    case 'CLEAR':
      return { selectedContract: null, hourlyPackage: null };
  }
}

export function useContractData(contractId: string | undefined) {
  const [{ selectedContract, hourlyPackage }, dispatchContract] = useReducer(contractReducer, {
    selectedContract: null,
    hourlyPackage: null,
  });

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!contractId) {
        if (!alive) return;
        dispatchContract({ type: 'CLEAR' });
        return;
      }

      try {
        const contract = await api.get<Contract>(`/contracts/${contractId}`);
        if (!alive) return;

        if (contract?.type === 'HOURLY' && contract?.hourlyPackageId) {
          const hp = await api.get<HourlyPackage>(`/hourly-packages/${contract.hourlyPackageId}`);
          if (!alive) return;
          dispatchContract({ type: 'SET_CONTRACT', contract, hourlyPackage: hp });
        } else {
          dispatchContract({ type: 'SET_CONTRACT', contract, hourlyPackage: null });
        }
      } catch {
        if (!alive) return;
        dispatchContract({ type: 'CLEAR' });
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [contractId]);

  return { selectedContract, hourlyPackage };
}

export type { Contract, HourlyPackage };
